import { useAuth0 } from "@auth0/auth0-react";
import * as Ably from "ably";
import { AblyProvider } from "ably/react";
import { createContext, useContext, useEffect, useState } from "react";
import { api } from "../lib/api";

interface AblyClientContextType {
	client: Ably.Realtime | null;
	isConnected: boolean;
}

const AblyClientContext = createContext<AblyClientContextType>({
	client: null,
	isConnected: false,
});

export const useAblyClient = () => useContext(AblyClientContext);

interface CustomAblyProviderProps {
	children: React.ReactNode;
}

export function CustomAblyProvider({ children }: CustomAblyProviderProps) {
	const { user, isLoading } = useAuth0();
	const [client, setClient] = useState<Ably.Realtime | null>(null);
	const [isConnected, setIsConnected] = useState(false);
	const [userDatabaseId, setUserDatabaseId] = useState<string | null>(null);
	const [isClientReady, setIsClientReady] = useState(false);

	// Get user database ID from the backend
	useEffect(() => {
		const fetchUserIdFromBackend = async () => {
			if (!user?.sub) {
				return;
			}

			try {
				const response = await api.get<{ user: { id: string } }>("/api/user");
				if (response.data?.user?.id) {
					setUserDatabaseId(response.data.user.id);
				}
			} catch (error) {
				console.error("Error fetching user database ID:", error);
			}
		};

		if (user && !isLoading) {
			fetchUserIdFromBackend();
		}
	}, [user, isLoading]);

	// Create Ably client with proper authentication
	useEffect(() => {
		if (!userDatabaseId || !import.meta.env.VITE_ABLY_API_KEY) {
			return;
		}

		const ablyClient = new Ably.Realtime({
			key: import.meta.env.VITE_ABLY_API_KEY,
			clientId: userDatabaseId,
		});

		// Connection state monitoring
		ablyClient.connection.on("connected", () => {
			setIsConnected(true);
		});

		ablyClient.connection.on("disconnected", () => {
			setIsConnected(false);
		});

		ablyClient.connection.on("failed", (error) => {
			console.error("Ably connection failed:", error);
			setIsConnected(false);
		});

		setClient(ablyClient);

		return () => {
			ablyClient.close();
		};
	}, [userDatabaseId]);

	// Mark client as ready after hydration
	useEffect(() => {
		setIsClientReady(true);
	}, []);

	// Show loading only after client is ready (hydrated)
	if (!isClientReady) {
		return <div>{children}</div>;
	}

	// Don't render Ably-dependent features until we have a client and user ID
	// This is fine - we'll fall back to polling
	if (!client || !userDatabaseId) {
		return (
			<AblyClientContext.Provider value={{ client: null, isConnected: false }}>
				{children}
			</AblyClientContext.Provider>
		);
	}

	return (
		<AblyProvider client={client}>
			<AblyClientContext.Provider value={{ client, isConnected }}>
				{children}
			</AblyClientContext.Provider>
		</AblyProvider>
	);
}
